import React, { useEffect, useMemo, useState } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Regulation,
  RegulationFormValues,
  ResponsibilityType,
  User,
} from '../DataTable/dataTypes';
import DocumentFields from './DocumentFields';
import RegulationFields from './RegulationFields';
import TaskFields from './Tasks/TaskFields';
import { fetchResponsibilities, fetchUsernames } from '../../api/api';
import LoadingModal from '../modals/LoadingModal/LoadingModal';
import ValidityFields from './ValidityFields';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import {
  addRegulationAsync,
  updateRegulationAsync,
} from '../../store/slices/regulationSlice';

interface RegulationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  regulation: Regulation | null;
  isReadOnly: boolean;
  deleteRegulation: (id: number) => void;
}

const RegulationFormDialog: React.FC<RegulationFormDialogProps> = ({
  open,
  onClose,
  onSave,
  regulation,
  isReadOnly,
  deleteRegulation
}) => {
  const [responsibilities, setResponsibilities] = useState<ResponsibilityType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const getUsernames = async () => {
      const usernames = await fetchUsernames();
      setUsers(usernames);
    };

    const getResponsibilities = async () => {
      const responsibilities = await fetchResponsibilities();
      setResponsibilities(responsibilities);
    };

    getUsernames();
    getResponsibilities();
  }, []);

  const defaultValues = useMemo(
    () => ({
      code: '',
      group: { id: 0 },
      emittedBy: { id: 0 },
      type: { id: 0 },
      scope: { id: 0 },
      category: { id: 0 },
      number: '',
      document: null,
      tasks: [],
      validity: true,      
      expirationReason: ''
    }),
    []
  );

  const methods = useForm<RegulationFormValues>({
    defaultValues,
    mode: 'all',
  });

  const { control, handleSubmit, reset, getValues } = methods;

  useEffect(() => {
    if (regulation) {
      const regulationForReset = {
        ...regulation,
        clientId: regulation.client?.id,
        groupId: regulation.group?.id,
        emittedById: regulation.emittedBy?.id,
        scopeId: regulation.scope?.id,
        typeId: regulation.type?.id,
        categoryId: regulation.category?.id,
      };
      reset(regulationForReset);
    } else {
      reset(defaultValues);
    }
  }, [regulation, reset, defaultValues]);

  const onSubmit: SubmitHandler<RegulationFormValues> = async (formData) => {
    setIsLoading(true);
    try {
      if (regulation?.id) {
        const updatedRegulation: Regulation = {
          ...formData,
          id: regulation.id,
        };
        await dispatch(updateRegulationAsync(updatedRegulation));
      } else { 
        // Actualiza el formulario en DB en los casos de adjuntar un documento 
        // y salvar la normativa antes de ser creada por onSubmit
        if(formData && 'id' in formData){
          const id = formData.id as number
          const updatedRegulation: Regulation = {
            ...formData,
            id: id,
          };
          await dispatch(updateRegulationAsync(updatedRegulation));
        }else{
          await dispatch(addRegulationAsync(formData));
        }        
      }
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setIsLoading(false);
      onClose(); 
    }
  };
  
  const saveFormBefore = (): Promise<Regulation> => {
    return new Promise<Regulation>((resolve, reject) => {
      handleSubmit(async (formData) => {
        setIsLoading(true)
        try {
          let updRegulation: Regulation

          if (regulation?.id) {
            const updatedRegulation: Regulation = {
              ...formData,
              id: regulation.id,
            }

            updRegulation = await dispatch(
              updateRegulationAsync(updatedRegulation)
            ).unwrap()
          } else {
            updRegulation = await dispatch(
              addRegulationAsync(formData)
            ).unwrap()
          }

          onSave()

          const regulationForReset = structuredClone({
            ...updRegulation,
            clientId: updRegulation.client?.id,
            groupId: updRegulation.group?.id,
            emittedById: updRegulation.emittedBy?.id,
            scopeId: updRegulation.scope?.id,
            typeId: updRegulation.type?.id,
            categoryId: updRegulation.category?.id,
          })
          reset(regulationForReset);

          resolve(updRegulation)
        } catch (error) {
          console.error('Error al guardar:', error)
          reject(error)
        } finally {
          setIsLoading(false)
        }
      })()
    })
  }

  const deleteNormativa = () => {
    const values = getValues()
    if (values && 'id' in values) {
      const id = values.id as number
      deleteRegulation(id)      
    }
    onClose();
  }

  return (
    <>
      {/* {isLoading && <LoadingModal open={true} />} */}
      <LoadingModal open={isLoading} />
      <Dialog
        // open={open && !isLoading}
        open={open}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(onSubmit),
        }}
      >
        <DialogTitle>
          {regulation
            ? isReadOnly
              ? 'Ver Normativa'
              : 'Editar Normativa'
            : 'Crear Normativa'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FormProvider {...methods}>
            <ValidityFields control={control} disabled={isReadOnly} />
            <RegulationFields control={control} disabled={isReadOnly} />
            <DocumentFields control={control} disabled={isReadOnly} />
            <TaskFields
              control={control}
              users={users}
              types={responsibilities}
              disabled={isReadOnly}
              saveFormBefore={saveFormBefore}
            />
          </FormProvider>
        </DialogContent>
        <DialogActions>          
          {!isReadOnly ?
            <>
              <Button onClick={regulation ? onClose : deleteNormativa} color="secondary">
                Cancelar
              </Button>
              <Button type="submit" color="secondary" disabled={isLoading}>
                {regulation ? 'Guardar Cambios' : 'Crear'}
              </Button>            
            </>
          :
          <Button onClick={onClose} color="secondary">
            Cancelar
          </Button>
          }
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegulationFormDialog;
